import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input } from './FormField';
import { Button } from '@/components/ui/Button';
import { profileApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EditProfileModal({ isOpen, onClose }: EditProfileModalProps) {
  const { user, checkAuth } = useAuth();

  const [formData, setFormData] = useState({ name: '', title: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill with current user data whenever modal opens
  useEffect(() => {
    if (isOpen && user) {
      setFormData({ name: user.name || '', title: user.title || '' });
      setErrors({});
    }
  }, [isOpen, user]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.name.trim().length > 100) newErrors.name = 'Name must be 100 characters or fewer';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await profileApi.update({
        name: formData.name.trim(),
        title: formData.title.trim() || undefined,
      });
      await checkAuth(); // refresh user in context so navbar name updates immediately
      onClose();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to save profile' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit profile" size="medium">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Your name"
          required
          error={errors.name}
        />

        <Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g. Product Manager"
          helpText="Optional"
        />

        {errors.submit && (
          <p className="text-sm text-red-600">{errors.submit}</p>
        )}

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
